// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User, signOut, deleteUser } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { getUserRole } from "../firebase/authorization/authorizedUsersRepository";

type UserRole = "admin" | "user" | null;

type AuthContextType = {
	user: User | null;
	loading: boolean;
	isAuthorized: boolean;
	isAdmin: boolean;
	userRole: UserRole;
	authorizationError: string | null;
};

const AuthContext = createContext<AuthContextType>({
	user: null,
	loading: true,
	isAuthorized: false,
	isAdmin: false,
	userRole: null,
	authorizationError: null
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [userRole, setUserRole] = useState<UserRole>(null);
	const [authorizationError, setAuthorizationError] = useState<string | null>(
		null
	);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
			setLoading(true);
			setUser(currentUser);

			if (currentUser && currentUser.email) {
				try {
					const role = await getUserRole(currentUser.email);
					const authorized = role !== null;
					setIsAuthorized(authorized);
					setUserRole(role);
					setIsAdmin(role === "admin");

					if (!authorized) {
						setAuthorizationError(
							"Your email is not authorized to access this application. Please contact the administrator."
						);
						// Delete unauthorized user from authentication
						try {
							await deleteUser(currentUser);
							console.log(
								"Unauthorized user deleted from authentication:",
								currentUser.email
							);
						} catch (deleteError) {
							console.warn(
								"Could not delete unauthorized user account:",
								deleteError
							);
							// Still sign out even if delete fails
							await signOut(auth);
						}
						setUser(null);
						setUserRole(null);
					} else {
						setAuthorizationError(null);
					}
				} catch (error) {
					console.error("Authorization check error:", error);
					setAuthorizationError(
						"An error occurred while checking authorization."
					);
					setIsAuthorized(false);
					setUserRole(null);
					// Sign out on authorization check error
					await signOut(auth);
					setUser(null);
				}
			} else {
				setIsAuthorized(false);
				setUserRole(null);
				setAuthorizationError(null);
			}

			setLoading(false);
		});
		return () => unsubscribe();
	}, []);

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				isAuthorized,
				isAdmin,
				userRole,
				authorizationError
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
